import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import LinkIcon from '$assets/svgs/Link.svg?react';
import DialogComponent from '~/components/DialogComponent';
import FrontdeskButton from '~/components/FrontdeskButton';
import {PAYMENT_DETAILS_STRINGS} from './constant';
import {
  dialogBodyStyles,
  dialogTitleStyles,
  dialogActionsContainerStyles,
  paymentLinkModalStyles,
  paymentLinkFieldStyles,
  copyButtonStyles,
  notificationSectionStyles,
  emailNotificationContainerStyles,
  smsNotificationContainerStyles,
  emailCheckboxStyles,
  smsCheckboxStyles,
  notificationInputStyles,
} from './styles';
import toast from '~/utils/toast';
import {ToastType} from '~/components/Toast/constant';

interface PaymentLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (data: {
    sendViaEmail: boolean;
    sendViaSms: boolean;
    email: string;
    phone: string;
  }) => void;
  paymentLink: string;
  email?: string;
  phone?: string;
  countryCode?: string;
  isLoading?: boolean;
}

const PaymentLinkModal: React.FC<PaymentLinkModalProps> = ({
  open,
  onClose,
  onSend,
  paymentLink,
  email: defaultEmail = '',
  phone: defaultPhone = '',
  countryCode = '+44',
  isLoading = false,
}) => {
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sendViaSms, setSendViaSms] = useState(false);
  const [email, setEmail] = useState(defaultEmail);

  const cleanPhoneNumber =
    defaultPhone && defaultPhone.startsWith(countryCode)
      ? defaultPhone.substring(countryCode.length)
      : defaultPhone || '';
  const [phone, setPhone] = useState(cleanPhoneNumber);

  useEffect(() => {
    setEmail(defaultEmail);
    const cleanPhone =
      defaultPhone && defaultPhone.startsWith(countryCode)
        ? defaultPhone.substring(countryCode.length)
        : defaultPhone || '';
    setPhone(cleanPhone);
  }, [defaultEmail, defaultPhone, countryCode]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink);
      toast(
        ToastType.Success,
        'Success',
        PAYMENT_DETAILS_STRINGS.PAYMENT_LINK_COPIED
      );
    } catch {
      toast(
        ToastType.Error,
        'Error',
        PAYMENT_DETAILS_STRINGS.PAYMENT_LINK_COPY_ERROR
      );
    }
  };

  const handleSend = () => {
    onSend({
      sendViaEmail,
      sendViaSms,
      email,
      phone: countryCode + phone,
    });
  };

  const handleClose = () => {
    setSendViaEmail(true);
    setSendViaSms(false);
    setEmail(defaultEmail);
    setPhone(cleanPhoneNumber);
    onClose();
  };

  return (
    <DialogComponent
      open={open}
      handleClose={handleClose}
      sx={paymentLinkModalStyles}
      dialogBody={
        <Box sx={dialogBodyStyles}>
          <Typography variant="h6" sx={dialogTitleStyles}>
            {PAYMENT_DETAILS_STRINGS.PAYMENT_LINK_MODAL_TITLE}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{fontWeight: 400, mb: 1, color: '#667085'}}
          >
            Here is the payment link for this invoice:
          </Typography>
          <Box sx={paymentLinkFieldStyles}>
            <TextField
              placeholder="Payment link"
              value={paymentLink}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              sx={notificationInputStyles}
            />
            <IconButton onClick={handleCopyLink} sx={copyButtonStyles}>
              <LinkIcon />
            </IconButton>
          </Box>

          <Box sx={notificationSectionStyles}>
            <Box sx={emailNotificationContainerStyles}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendViaEmail}
                    onChange={e => setSendViaEmail(e.target.checked)}
                  />
                }
                label="Notify the patient about their payment link via email"
                sx={emailCheckboxStyles}
              />
              <TextField
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                variant="outlined"
                InputProps={{
                  readOnly: false,
                }}
                sx={notificationInputStyles}
              />
            </Box>

            <Box sx={smsNotificationContainerStyles}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendViaSms}
                    onChange={e => setSendViaSms(e.target.checked)}
                  />
                }
                label="Notify the patient about their payment link via SMS"
                sx={smsCheckboxStyles}
              />
              <TextField
                placeholder="Phone number"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                variant="outlined"
                InputProps={{
                  readOnly: false,
                  startAdornment: (
                    <Typography
                      sx={{mr: 1, color: '#667085', fontSize: '14px'}}
                    >
                      {countryCode}
                    </Typography>
                  ),
                }}
                sx={notificationInputStyles}
              />
            </Box>
          </Box>

          <Box sx={dialogActionsContainerStyles}>
            <FrontdeskButton
              variant="secondary"
              title="Cancel"
              onClick={handleClose}
              className="cancel-red"
            />
            <FrontdeskButton
              variant="primary"
              title={PAYMENT_DETAILS_STRINGS.SEND_PAYMENT_LINK}
              onClick={handleSend}
              loading={isLoading}
              disabled={!sendViaEmail && !sendViaSms}
            />
          </Box>
        </Box>
      }
    />
  );
};

export default PaymentLinkModal;
